/**
 * 표면 신호 — 식별자 없는 발견·활성화 집계.
 *
 * 이벤트 이름은 `src/domain`이 소유하고 화면·채널 코드는 이를 소비한다(AGENTS.md).
 *
 * 2026-08-11 실측: `https://bonaelyeonju.com` 첫 방문
 * 8.9초 동안 계측 요청 0건. 배관 결함이 아니라 부재였다 — 저장소에 계측 코드가
 * 한 줄도 없었고 수집 경로도 없었다. 계측이 0건이면 `/pulse`는 discovery도
 * activation도 판정할 수 없고, 제품을 늘려도 판단 재료가 늘지 않는다.
 *
 * 제품 불변조건과의 관계: 원본 영상·Blob·파일명·지문은 이 신호에 넣지 않는다.
 * 담기는 값은 신호 이름·제품·시각과 허용 목록의 유입 차원뿐이라 한 방문을
 * 다른 방문과 구분할 수 없다. URL 전체·검색어·referrer host는 담지 않는다.
 */
export const SURFACE_SIGNALS = [
  'first_value', 'revisit_7d', 'share_card', 'purchase_intent', 'preset_applied', 'calendar_export', 'app_installed',
  'app_open',
  'landing_view',
  'guide_view',
  'guide_sample',
  'guide_start',
  'activation_start',
  'activation_done',
] as const;

export type SurfaceSignal = (typeof SURFACE_SIGNALS)[number];

export interface SurfaceSignalPayload {
  name: SurfaceSignal;
  productId: 'audition-take-preflight';
  occurredAt: string;
  entry?: EntryContext;
  decided?: boolean;
  presetId?: string;
}

export interface EntryContext {
  entryPath: 'home' | 'guide';
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  referrerKind: 'direct' | 'internal' | 'search' | 'social' | 'referral';
}

const SAFE_CAMPAIGN_VALUES = new Set(['naver','google','bing','daum','kakao','kakaotalk','share_card','teacher','community','organic','search','social','referral','direct','internal','video-checklist','completion-2026-10','slideroom','acceptd','video-size','recording']);

function campaignValue(value: string | null): string | undefined {
  return value && SAFE_CAMPAIGN_VALUES.has(value) ? value : undefined;
}

function referrerKind(referrer: string, currentOrigin: string): EntryContext['referrerKind'] {
  if (!referrer) return 'direct';
  try {
    const url = new URL(referrer);
    if (url.origin === currentOrigin) return 'internal';
    const host = url.hostname.toLowerCase();
    if (/(^|\.)(google|bing|naver|daum)\./.test(host)) return 'search';
    if (/(^|\.)(instagram|threads|youtube|youtu|facebook|x|twitter)\./.test(host)) return 'social';
  } catch {
    return 'direct';
  }
  return 'referral';
}

export function entryContextFromLocation(pathname: string, search: string, referrer: string, currentOrigin: string): EntryContext {
  const params = new URLSearchParams(search);
  return {
    entryPath: pathname.startsWith('/guides/') ? 'guide' : 'home',
    utmSource: campaignValue(params.get('utm_source')),
    utmMedium: campaignValue(params.get('utm_medium')),
    utmCampaign: campaignValue(params.get('utm_campaign')),
    referrerKind: referrerKind(referrer, currentOrigin),
  };
}

export function isSurfaceSignal(value: unknown): value is SurfaceSignal {
  return typeof value === 'string' && (SURFACE_SIGNALS as readonly string[]).includes(value);
}

export function parseSurfaceSignal(payload: unknown): SurfaceSignalPayload | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const candidate = payload as Record<string, unknown>;
  if (!isSurfaceSignal(candidate.name)) return null;
  if (candidate.productId !== 'audition-take-preflight') return null;
  if (typeof candidate.occurredAt !== 'string' || Number.isNaN(Date.parse(candidate.occurredAt))) {
    return null;
  }
  let entry: EntryContext | undefined;
  if (candidate.entry !== undefined) {
    if (typeof candidate.entry !== 'object' || candidate.entry === null) return null;
    const raw = candidate.entry as Record<string, unknown>;
    if (!['home', 'guide'].includes(String(raw.entryPath))) return null;
    if (!['direct', 'internal', 'search', 'social', 'referral'].includes(String(raw.referrerKind))) return null;
    for (const key of ['utmSource', 'utmMedium', 'utmCampaign'] as const) {
      if (raw[key] !== undefined && (typeof raw[key] !== 'string' || !SAFE_CAMPAIGN_VALUES.has(raw[key]))) return null;
    }
    entry = {
      entryPath: raw.entryPath as EntryContext['entryPath'],
      referrerKind: raw.referrerKind as EntryContext['referrerKind'],
      ...(raw.utmSource ? { utmSource: raw.utmSource as string } : {}),
      ...(raw.utmMedium ? { utmMedium: raw.utmMedium as string } : {}),
      ...(raw.utmCampaign ? { utmCampaign: raw.utmCampaign as string } : {}),
    };
  }
  return {
    name: candidate.name,
    productId: 'audition-take-preflight',
    occurredAt: candidate.occurredAt,
    ...(entry ? { entry } : {}),
    ...(typeof candidate.decided === 'boolean' ? { decided: candidate.decided } : {}),
    ...(['slideroom','acceptd','youtube_link','google_forms','school'].includes(String(candidate.presetId)) ? { presetId: String(candidate.presetId) } : {}),
  };
}
